import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { IJwtDecoded } from "@app/auth/models/iJwtDecoded";
import { ILoginResponse } from "@app/auth/models/iLoginResponse.model";
import { IPassword } from "@app/auth/models/iPassword.model";
import { Profile } from "@app/core/models/Profile";
import { Role } from "@app/core/models/Role";
import { AdminService } from "@app/core/services/admin/admin.service";
import { CompanyService } from "@app/core/services/company/company.service";
import { DistributorService } from "@app/distributor/services/distributor/distributor.service";
import { FeedbackType } from "@app/shared/models/feedbackType";
import { IApiResponse } from "@app/shared/models/iApiResponse.model";
import { IPasswordResponse } from "@app/shared/models/iPasswordResponse.model";
import { ErrorService } from "@app/shared/services/error/error.service";
import { FeedbackService } from "@app/shared/services/feedback/feedback.service";
import { SessionService } from "@app/shared/services/session/session.service";
import { Api } from "@app-shared/config/Api";
import { jwtDecode } from "jwt-decode";
import { catchError, tap, throwError } from "rxjs";
import { finalize } from "rxjs/operators";

import { ILogin } from "../../models/iLogin.model";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private defaultError = inject(ErrorService);
  private distributorService = inject(DistributorService);
  private adminService = inject(AdminService);
  private companyService = inject(CompanyService);
  private feedback = inject(FeedbackService);
  private sessionService = inject(SessionService);

  loc = localStorage;
  api = new Api();

  login(loginData: ILogin) {
    return this.http
      .post<ILoginResponse>(`${this.api.getHostV1()}/signin/`, loginData)
      .pipe(
        catchError(this.defaultError.handleError),
        tap((response) => {
          this.storeUserData(response.accessToken, response.refreshToken);
        }),
        tap(() => {
          if (this.userRole === Role.DISTRIBUTOR) {
            this.getDistributorData();
            this.getCompanyData();
          } else if (this.userRole === Role.ADMIN) {
            this.getAdminData();
          }
        }),
        finalize(() => {
          const role = this.loc.getItem("role");
          this.redirectToRoleRoute(role!);
        })
      );
  }

  autoLogin() {
    if (this.isLoggedIn()) {
      if (this.userRole === Role.DISTRIBUTOR) {
        this.router.navigate(["/distributor"]);
        return true;
      }

      this.router.navigate(["/admin"]);
      return true;
    }
    return false;
  }

  logout() {
    const userData: Partial<ILoginResponse> = {
      refreshToken: this.getRefreshToken!,
    };

    return this.http
      .post<ILoginResponse>(`${this.api.getHostV1()}/logout/`, userData)
      .pipe(
        catchError(this.defaultError.handleError),
        tap(() => {
          localStorage.clear();
        }),
        finalize(() => {
          this.router.navigate(["/login"]);
        })
      );
  }

  storeUserData(accessToken: string, refreshToken: string) {
    const decodedAccessData: IJwtDecoded = jwtDecode(accessToken);
    const decodedRefreshData: IJwtDecoded = jwtDecode(refreshToken);

    this.loc.setItem("access_exp", decodedAccessData.exp.toString());
    this.loc.setItem("refresh_exp", decodedRefreshData.exp.toString());
    this.loc.setItem("role", decodedAccessData.role);
    this.loc.setItem("user_id", decodedAccessData.user_id);
    this.loc.setItem("company_id", decodedAccessData.company_id);
    this.loc.setItem("access_token", accessToken);
    this.loc.setItem("refresh_token", refreshToken);
    this.loc.setItem("profiles", JSON.stringify(decodedAccessData.profiles));
  }

  changePassword(passwordData: IPassword) {
    return this.http
      .post(`${this.api.getHostV1()}/password/change/`, passwordData)
      .pipe(
        catchError(this.defaultError.handleError),
        tap(() => {
          localStorage.clear();
        }),
        finalize(() => {
          this.router.navigate(["/login"]);
        })
      );
  }

  resetPassword(email: string) {
    return this.http
      .post<IApiResponse>(`${this.api.getHostV1()}/password/request-reset`, {
      email,
    })
      .pipe(catchError(this.defaultError.handleError));
  }

  validatePasswordToken(token: string) {
    return this.http
      .post(`${this.api.getHostV1()}/password/reset/validate_token/`, {
        token,
      })
      .pipe(catchError(this.defaultError.handleError));
  }

  updatePassword(password: string, token: string) {
    return this.http
      .post<IPasswordResponse>(
      `${this.api.getHostV1()}/password/reset/custom-confirm`,
      {
        password,
        token,
      }
    )
      .pipe(catchError(this.defaultError.handleError));
  }

  get userRole(): string {
    return this.loc.getItem("role")!;
  }

  get getAccessToken() {
    return this.loc.getItem("access_token");
  }

  get getRefreshToken() {
    return this.loc.getItem("refresh_token");
  }

  get getUserId() {
    return this.loc.getItem("user_id");
  }

  get getCompanyId() {
    return this.loc.getItem("company_id");
  }

  get isSuperVisor(): boolean {
    return !!(
      this.userRole === Role.INSTRUMENT_TECHNICIAN
      && this.loc.getItem("profiles")?.includes(Profile.SUPERVISOR)
    );
  }
  isTokenExpired(tokenKey: string) {
    const exp = this.loc.getItem(tokenKey);
    if (!exp) {
      return true;
    }
    const expirationDate = new Date(parseInt(exp, 10) * 1000);
    return expirationDate <= new Date();
  }

  isLoggedIn(): boolean {
    const access = "access_exp";
    const refresh = "refresh_exp";

    if (!this.isTokenExpired(access)) {
      return true;
    }
    return !this.isTokenExpired(refresh);
  }

  refreshToken() {
    const refreshToken = this.loc.getItem("refresh_token");
    if (!refreshToken) {
      return throwError(() => new Error("Refresh token not found"));
    }

    return this.http.post<ILoginResponse>(
      `${this.api.getHostV1()}/token/refresh/`,
      {
        refresh: refreshToken,
      }
    );
  }

  private getDistributorData() {
    this.distributorService.getUserById(this.getUserId as string).subscribe({
      next: (data) => {
        this.loc.setItem("userName", data.name as string);
      },

      error: () => {
        catchError(this.defaultError.handleError);
      },
    });
  }

  private getAdminData() {
    this.adminService.getUserById(this.getUserId as string).subscribe({
      next: (data) => {
        this.loc.setItem("userName", data.name as string);
      },

      error: () => {
        catchError(this.defaultError.handleError);
      },
    });
  }

  private getCompanyData() {
    this.companyService.getCompanyById(this.getCompanyId as string).subscribe({
      next: (data) => {
        this.loc.setItem("companyName", data.tradingName as string);
      },

      error: () => {
        catchError(this.defaultError.handleError);
      },
    });
  }

  private redirectToRoleRoute(role: string) {
    const roleRoutes = {
      ADMIN: "/admin",
      DISTRIBUTOR: "/distributor",
      QUALITY: "/quality/analysis",
    };

    // Perhaps in the future we also need a special route for the technician which is a Supervisor,
    // since he will be able to create appointments.

    switch (role) {
      case Role.ADMIN:
        this.router.navigate([roleRoutes.ADMIN]);
        break;

      case Role.DISTRIBUTOR:
        this.router.navigate([roleRoutes.DISTRIBUTOR]);
        break;

      case Role.QUALITY:
        this.router.navigate([
          roleRoutes.QUALITY,
          this.sessionService.generateSessionId(),
        ]);
        break;

      case Role.INSTRUMENT_TECHNICIAN:
        if (this.isSuperVisor) {
          this.displaySupervisorError();
        } else {
          this.router.navigate([roleRoutes.QUALITY]);
        }
        break;

      default:
        this.router.navigate(["login"]);
        break;
    }
  }

  resendEmail(email: string) {
    return this.http
      .post<IApiResponse>(`${this.api.getHostV1()}/password/send-invite`, {
      email,
    })
      .pipe(catchError(this.defaultError.handleError));
  }

  private displaySupervisorError(): void {
    localStorage.clear();
    this.feedback.openModal(
      FeedbackType.ERROR,
      "Oops!",
      "Você não tem permissão para acessar esta página."
    );
  }
}
